import { getContactById, getContactDisplayName, listDossierContacts } from '../contacts/contactService';
import { listDossiers } from '../dossiers/dossierService';
import {
  createBillingDocument,
  getBillingDocument,
  listLineItems,
  replaceBillingLines,
  updateBillingDocumentMeta,
  type BillingDocument,
  type BillingLineItem,
} from '../billing/billingService';
import { upsertPolarDraft, type CimeContactParty } from '../billing/polarClient';
import { contactToPolarParty, createPolarClientDeps } from '../billing/polarBilling';
import type { DraftCall, NovaHit, NovaSession } from './novaSession';
import { polarKind } from './novaSession';
import type { NovaSheet } from './novaLoop';

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00`);
  date.setDate(date.getDate() + days);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function noteText(call: DraftCall): string {
  return [call.title, call.notes].filter(Boolean).join('\n');
}

function dossierLabel(session: NovaSession, dossierId: string | null): string | null {
  if (!dossierId) return null;
  return session.dossiers.find((d) => d.id === dossierId)?.label ?? null;
}

export async function searchNovaDossiers(query: string): Promise<NovaHit[]> {
  const rows = await listDossiers({ search: query || undefined });
  return rows.slice(0, 8).map((d) => ({ id: d.id, label: `${d.number} — ${d.title}` }));
}

export async function searchNovaClients(dossierId: string, query: string): Promise<NovaHit[]> {
  const needle = query.trim().toLowerCase();
  const rows = await listDossierContacts(dossierId);
  return rows
    .filter((row) => row.role === 'client' && row.contact)
    .map((row) => ({ id: row.contact.id, label: getContactDisplayName(row.contact) }))
    .filter((row) => !needle || row.label.toLowerCase().includes(needle))
    .slice(0, 8);
}

export async function persistNovaDraft(
  session: NovaSession,
  call: DraftCall,
): Promise<{ session: NovaSession; sheet: NovaSheet; summary: string }> {
  const due = addDays(today(), call.dueDays);
  const notes = noteText(call);
  let doc: BillingDocument;
  if (call.creating || !session.documentId) {
    if (!call.dossierId || !call.contactId) throw new Error('Dossier et client requis.');
    doc = await createBillingDocument({
      dossier_id: call.dossierId,
      doc_type: call.kind,
      client_contact_id: call.contactId,
      vat_rate: call.vatRate,
      due_date: due,
      notes: notes || null,
    });
  } else {
    doc = await updateBillingDocumentMeta(session.documentId, {
      notes: notes || null,
      due_date: due,
      vat_rate: call.vatRate,
    });
  }

  let lines: BillingLineItem[] = [];
  if (call.lines.length > 0) {
    lines = await replaceBillingLines(
      doc.id,
      call.lines.map((line, index) => ({
        description: line.description,
        quantity: line.quantity,
        unit: 'item' as const,
        unit_price: line.unit_price,
        sort_order: index + 1,
      })),
    );
    const reloaded = await getBillingDocument(doc.id);
    if (reloaded) doc = reloaded;
  }

  const contactId = doc.client_contact_id;
  if (!contactId) throw new Error('Client manquant sur le brouillon.');
  const contact = await getContactById(contactId);
  if (!contact) throw new Error('Client introuvable.');

  const polarExisting =
    call.kind === 'invoice' ? doc.polar_invoice_id : doc.polar_quote_id;
  let polarId = polarExisting;
  let polarNote = 'Polaris : brouillon enregistré.';
  try {
    const pushed = await upsertPolarDraft(
      {
        documentId: doc.id,
        kind: polarKind(call.kind),
        polarId: polarExisting,
        contact: contactToPolarParty(contact) satisfies CimeContactParty,
        title: call.title || notes || (call.kind === 'invoice' ? 'Facture' : 'Devis'),
        notes: call.notes,
        lines: call.lines,
        vatRate: call.vatRate,
        serviceDate: doc.issue_date,
        dueDays: call.dueDays,
      },
      createPolarClientDeps(),
    );
    polarId = pushed.polar_id;
    const refreshed = await getBillingDocument(doc.id);
    if (refreshed) doc = refreshed;
    if (lines.length === 0) lines = await listLineItems(doc.id);
  } catch (err) {
    polarNote = err instanceof Error ? `Polaris : ${err.message}` : 'Polaris injoignable.';
  }

  const next: NovaSession = {
    ...session,
    creation: true,
    kind: call.kind,
    documentId: doc.id,
    polarId,
  };
  const label = dossierLabel(session, doc.dossier_id);
  return {
    session: next,
    sheet: { doc, lines, dossierLabel: label },
    summary: `Brouillon ${call.kind === 'invoice' ? 'facture' : 'devis'} ${doc.id}. ${polarNote} Rien n'est numéroté.`,
  };
}
